import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { BaseComponent } from '../../base/base.component';
import { FunctionService } from '../../services/function.service';
import { NotificationService } from '../../services/notification.service';
import {
  DiscrepancyListService,
  DiscrepancyClientConfig,
  OprClientOption,
  OprWarehouseOption,
  DiscrepancyRow,
  DiscrepancySearchRequest,
  DiscrepancySummary
} from '../../services/discrepancy-list.service';
import FileSaver from 'file-saver';
import { SearchableSelectComponent, SearchableSelectOption } from '../../components/searchable-select/searchable-select.component';
import flatpickr from 'flatpickr';
import { Italian } from 'flatpickr/dist/l10n/it';
import { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';

// ─── TABLE INTERFACES ───
export interface DiscrepancyColumn {
  key: string;
  label: string;
  visible: boolean;
  sortable?: boolean;
  type?: 'text' | 'date' | 'number' | 'badge';
}

// Mapping nomi colonna dal backend → definizione colonna UI
const COLUMN_MAP: Record<string, Omit<DiscrepancyColumn, 'visible'>> = {
  'Waybill':     { key: 'waybillCode',  label: 'Bolla',        sortable: true,  type: 'text'   },
  'Parcel':      { key: 'parcelCode',   label: 'Collo',         sortable: true,  type: 'text'   },
  'Pallet':      { key: 'palletCode',   label: 'Pallet',        sortable: true,  type: 'text'   },
  'Status':      { key: 'status',       label: 'Stato',         sortable: true,  type: 'badge'  },
  'Date':        { key: 'date',         label: 'Data',          sortable: true,  type: 'date'   },
  'Destination': { key: 'destination',  label: 'Destinazione',  sortable: true,  type: 'text'   },
  'Carrier':     { key: 'carrierCode',  label: 'Corriere',      sortable: true,  type: 'text'   },
  'Quantity':    { key: 'quantity',     label: 'Quantità',      sortable: true,  type: 'number' },
  'Type':        { key: 'type',         label: 'Tipo',          sortable: true,  type: 'badge'  },
  'Notes':       { key: 'notes',        label: 'Note',          sortable: false, type: 'text'   },
};

@Component({
  selector: 'app-discrepancy-list',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, SearchableSelectComponent],
  templateUrl: './discrepancy-list.component.html',
  styleUrl: './discrepancy-list.component.css'
})
export class DiscrepancyListComponent extends BaseComponent implements OnInit, OnDestroy {

  constructor(
    functionService: FunctionService,
    router: Router,
    private discrepancyService: DiscrepancyListService,
    private notificationService: NotificationService
  ) {
    super(functionService, router);
  }

  // ─── SECTION COLLAPSE ───
  isFiltersCollapsed = false;
  isReportCollapsed  = false;

  // ─── DATI DAI 3 ENDPOINT ───
  /** Opzioni searchable-select cliente (da /clients → Opr_Clients) */
  clientSelectOptions: SearchableSelectOption[] = [];
  /** Opzioni searchable-select piattaforma (da /warehouses → Opr_Warehouses) */
  warehouseSelectOptions: SearchableSelectOption[] = [];
  /** Configurazione per-cliente (da /configuration → configurationValue JSON) */
  configClients: DiscrepancyClientConfig[] = [];

  // ─── FILTERS ───
  selectedClientCode    = '';
  selectedWarehouseCode = '';
  dateFrom: Date | null = null;
  dateTo:   Date | null = null;

  // ─── FLATPICKR ───
  @ViewChild('dateFromInput') dateFromInput!: ElementRef<HTMLInputElement>;
  @ViewChild('dateToInput')   dateToInput!:   ElementRef<HTMLInputElement>;
  private fpFrom: FlatpickrInstance | null = null;
  private fpTo:   FlatpickrInstance | null = null;

  // ─── SUMMARY ───
  summary: DiscrepancySummary = this.emptySummary();
  isLoadingSummary = false;

  // ─── DOWNLOAD EXCEL / IMMAGINI ───
  isDownloadingExcel  = false;
  isDownloadingImages = false;

  // ─── TABLE ───
  columns: DiscrepancyColumn[] = [];
  rows: DiscrepancyRow[] = [];
  isLoading = false;
  hasSearchBeenPerformed = false;
  errorMessage = '';

  // ─── AZIONE: INVIO LOADED TO CLIENT ───
  isSendingLoaded = false;

  // ─── AZIONE: INVIO UPDATE AL CLIENTE ───
  isSendingUpdates = false;

  // ─── AZIONI: INVIO SHIPMENTS / CLAIMS / REASONCODE ───
  isSendingShipments  = false;
  isSendingClaims     = false;
  isSendingReasonCode = false;

  // ─── AZIONE: IMPORT ALLEGATI DDT DA CARTELLA ───
  isImportingDdtAttachments = false;

  // ─── AZIONE: EXPORT XML SU CARTELLA ───
  isExportingXml = false;

  // ─── FLASH CAMPI MODIFICATI DA SELEZIONE VELOCE ───
  flashClient = false;
  flashWarehouse = false;
  private flashTimer: any = null;

  // ─── SORT ───
  sortField = '';
  sortAsc   = true;

  // ─── PAGINATION ───
  currentPage  = 1;
  itemsPerPage = 25;
  totalRecords = 0;

  Math = Math;

  // ─── COMPUTED ───
  get visibleColumns(): DiscrepancyColumn[] {
    return this.columns.filter(c => c.visible);
  }

  // ─── LIFECYCLE ───
  ngOnInit(): void {
    this.initDefaultDates();
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.destroyFlatpickr();
    if (this.flashTimer) { clearTimeout(this.flashTimer); }
  }

  // ─── DATES ───
  private initDefaultDates(): void {
    const today = new Date();
    this.dateFrom = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0,  0, 0);
    this.dateTo   = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 0);
  }

  private initFlatpickr(): void {
    this.destroyFlatpickr();
    if (this.dateFromInput?.nativeElement) {
      this.fpFrom = flatpickr(this.dateFromInput.nativeElement, {
        dateFormat: 'd/m/Y H:i', enableTime: true, time_24hr: true, locale: Italian,
        defaultDate: this.dateFrom ?? undefined,
        onChange: (dates: Date[]) => { this.dateFrom = dates[0] ?? null; }
      });
    }
    if (this.dateToInput?.nativeElement) {
      this.fpTo = flatpickr(this.dateToInput.nativeElement, {
        dateFormat: 'd/m/Y H:i', enableTime: true, time_24hr: true, locale: Italian,
        defaultDate: this.dateTo ?? undefined,
        onChange: (dates: Date[]) => { this.dateTo = dates[0] ?? null; }
      });
    }
  }

  private destroyFlatpickr(): void {
    this.fpFrom?.destroy(); this.fpFrom = null;
    this.fpTo?.destroy();   this.fpTo   = null;
  }

  // ─── INITIAL DATA LOAD ───
  private loadInitialData(): void {
    // Tutti e 3 gli observable gestiscono i propri errori → forkJoin risolve sempre
    forkJoin({
      clients:    this.discrepancyService.getClients(),
      warehouses: this.discrepancyService.getWarehouses(),
      config:     this.discrepancyService.getConfiguration()
    }).subscribe(({ clients, warehouses, config }) => {
      // Dropdown clienti: da /clients (Opr_Clients: clientCode + clientName)
      this.clientSelectOptions = (clients ?? []).map(c => ({
        value: c.clientCode,
        label: c.clientName || c.clientCode
      }));
      // Dropdown piattaforme: da /warehouses (Opr_Warehouses: warehouseCode + warehouseName)
      this.warehouseSelectOptions = (warehouses ?? []).map(w => ({
        value: w.warehouseCode,
        label: w.warehouseName || w.warehouseCode
      }));
      // Config per quickset + colonne: da /configuration → configurationValue JSON
      this.configClients = config.clients ?? [];

      // Se c'è un solo cliente lo pre-seleziona con il suo quickset
      if (this.configClients.length === 1) {
        this.applyClientQuickset(this.configClients[0]);
      } else if (this.clientSelectOptions.length === 1) {
        this.selectedClientCode = this.clientSelectOptions[0].value;
        this.onClientChange();
      }

      setTimeout(() => this.initFlatpickr(), 0);
    });
  }

  private mapColumnNames(names: string[]): DiscrepancyColumn[] {
    return names
      .filter(n => COLUMN_MAP[n])
      .map(n => ({ ...COLUMN_MAP[n], visible: true }));
  }

  // ─── QUICKSET PER CLIENTE ───
  applyClientQuickset(client: DiscrepancyClientConfig): void {
    const prevClient    = this.selectedClientCode;
    const prevWarehouse = this.selectedWarehouseCode;

    this.selectedClientCode    = client.code;
    this.selectedWarehouseCode = client.warehouses.length === 1
      ? client.warehouses[0].code
      : '';
    this.columns = this.mapColumnNames(client.columns);

    // Evidenzia con un lampeggio i campi effettivamente cambiati
    this.triggerFieldFlash(
      this.selectedClientCode    !== prevClient,
      this.selectedWarehouseCode !== prevWarehouse
    );
  }

  /** Fa lampeggiare i bordi dei campi cambiati dalla selezione veloce */
  private triggerFieldFlash(clientChanged: boolean, warehouseChanged: boolean): void {
    if (this.flashTimer) { clearTimeout(this.flashTimer); }
    // Reset per consentire il riavvio dell'animazione anche su click ravvicinati
    this.flashClient = false;
    this.flashWarehouse = false;
    setTimeout(() => {
      this.flashClient = clientChanged;
      this.flashWarehouse = warehouseChanged;
    }, 0);
    // Rimuove le classi al termine dell'animazione
    this.flashTimer = setTimeout(() => {
      this.flashClient = false;
      this.flashWarehouse = false;
      this.flashTimer = null;
    }, 950);
  }

  /** Chiamato quando l'utente cambia il select cliente manualmente */
  onClientChange(): void {
    // Aggiorna solo le colonne dalla configurazione per-cliente (se disponibile)
    // Il select piattaforma è indipendente: non viene resettato
    const cfg = this.configClients.find(c => c.code === this.selectedClientCode);
    this.columns = cfg ? this.mapColumnNames(cfg.columns) : [];
  }

  // ─── INVIO LOADED TO CLIENT ───
  sendLoadedToClient(): void {
    if (this.isSendingLoaded) return;
    this.isSendingLoaded = true;
    this.discrepancyService.sendLoadedToClient().pipe(
      finalize(() => { this.isSendingLoaded = false; })
    ).subscribe({
      next: res => {
        this.notificationService.success(
          `Invio completato: ${res.sent}/${res.processed} bolle inviate al cliente ${res.clientCode}.`
        );
      },
      error: err => {
        const msg = err?.error?.error || err?.message || 'Errore durante l\'invio delle bolle al cliente.';
        this.notificationService.error(msg);
      }
    });
  }

  // ─── EXPORT XML SU CARTELLA ───
  exportXmlToFolder(): void {
    if (this.isExportingXml) return;
    this.isExportingXml = true;
    this.discrepancyService.exportXmlToFolder().pipe(
      finalize(() => { this.isExportingXml = false; })
    ).subscribe({
      next: res => {
        this.notificationService.success(
          `Export completato: ${res.xmlGenerated} XML generati su ${res.waybillsFound} bolle (cliente ${res.clientCode}).`
        );
        const notFound = res.shipmentNotFound?.length || 0;
        if (notFound) {
          this.notificationService.warning(
            `Attenzione: ${notFound} spedizioni non trovate, XML non generati.`
          );
        }
      },
      error: err => {
        const msg = err?.error?.error || err?.message || 'Errore durante la generazione degli XML.';
        this.notificationService.error(msg);
      }
    });
  }

  // ─── IMPORT ALLEGATI DDT DA CARTELLA ───
  importDdtAttachments(): void {
    if (this.isImportingDdtAttachments) return;
    this.isImportingDdtAttachments = true;
    this.discrepancyService.importDdtAttachmentsFromFolder().pipe(
      finalize(() => { this.isImportingDdtAttachments = false; })
    ).subscribe({
      next: res => {
        this.notificationService.success(
          `Import completato: ${res.waybillsUpdated} bolle aggiornate da ${res.filesProcessed} file (${res.rowsParsed} righe).`
        );
        const notFound = res.notFoundWaybills?.length || 0;
        const skipped  = res.skippedRows?.length || 0;
        const failed   = res.failedFiles?.length || 0;
        if (notFound || skipped || failed) {
          this.notificationService.warning(
            `Attenzione: ${notFound} bolle non trovate, ${skipped} righe saltate, ${failed} file in errore.`
          );
        }
      },
      error: err => {
        const msg = err?.error?.error || err?.message || 'Errore durante l\'import degli allegati DDT.';
        this.notificationService.error(msg);
      }
    });
  }

  // ─── INVIO UPDATE AL CLIENTE ───
  sendUpdatesToClient(): void {
    if (this.isSendingUpdates) return;
    this.isSendingUpdates = true;
    this.discrepancyService.sendUpdatesToClient().pipe(
      finalize(() => { this.isSendingUpdates = false; })
    ).subscribe({
      next: res => {
        this.notificationService.success(
          `Update inviati al cliente ${res.clientCode} (operazione ${res.operation}).`
        );
      },
      error: err => {
        const msg = err?.error?.error || err?.message || 'Errore durante l\'invio degli update al cliente.';
        this.notificationService.error(msg);
      }
    });
  }

  // ─── INVIO SHIPMENTS (colli) ───
  sendShipments(): void {
    if (this.isSendingShipments) return;
    this.isSendingShipments = true;
    this.discrepancyService.sendShipments().pipe(
      finalize(() => { this.isSendingShipments = false; })
    ).subscribe({
      next: res => {
        this.notificationService.success(
          `Shipments inviati al cliente ${res.clientCode} (operazione ${res.operation}).`
        );
      },
      error: err => {
        const msg = err?.error?.error || err?.message || 'Errore durante l\'invio degli shipments al cliente.';
        this.notificationService.error(msg);
      }
    });
  }

  // ─── INVIO CLAIMS (pallet) ───
  sendClaims(): void {
    if (this.isSendingClaims) return;
    this.isSendingClaims = true;
    this.discrepancyService.sendClaims().pipe(
      finalize(() => { this.isSendingClaims = false; })
    ).subscribe({
      next: res => {
        this.notificationService.success(
          `Claims inviati al cliente ${res.clientCode} (operazione ${res.operation}).`
        );
      },
      error: err => {
        const msg = err?.error?.error || err?.message || 'Errore durante l\'invio dei claims al cliente.';
        this.notificationService.error(msg);
      }
    });
  }

  // ─── INVIO REASONCODE ───
  sendReasonCode(): void {
    if (this.isSendingReasonCode) return;
    this.isSendingReasonCode = true;
    this.discrepancyService.sendReasonCode().pipe(
      finalize(() => { this.isSendingReasonCode = false; })
    ).subscribe({
      next: res => {
        this.notificationService.success(
          `Reason code inviati al cliente ${res.clientCode} (operazione ${res.operation}).`
        );
      },
      error: err => {
        const msg = err?.error?.error || err?.message || 'Errore durante l\'invio dei reason code al cliente.';
        this.notificationService.error(msg);
      }
    });
  }

  // ─── SEARCH ───
  search(): void {
    this.currentPage = 1;
    this.hasSearchBeenPerformed = true;
    this.loadData();
  }

  private buildSearchRequest(): DiscrepancySearchRequest {
    return {
      clientCode:    this.selectedClientCode    || null,
      warehouseCode: this.selectedWarehouseCode || null,
      dateFrom:      this.dateFrom ? this.dateFrom.toISOString() : null,
      dateTo:        this.dateTo   ? this.dateTo.toISOString()   : null,
      page:          this.currentPage - 1,
      size:          this.itemsPerPage,
      sortField:     this.sortField,
      sortAsc:       this.sortAsc
    };
  }

  private loadData(): void {
    this.isLoading        = true;
    this.isLoadingSummary = true;
    this.errorMessage     = '';
    this.discrepancyService.getSummary(this.buildSearchRequest()).pipe(
      finalize(() => { this.isLoading = false; this.isLoadingSummary = false; })
    ).subscribe({
      next: summary => { this.summary = summary; },
      error: () => {
        this.summary      = this.emptySummary();
        this.errorMessage = 'Errore nel caricamento del resoconto. Riprova più tardi.';
      }
    });
  }

  private emptySummary(): DiscrepancySummary {
    return {
      colliAffidati: 0, colliPickerati: 0, colliNonPickerati: 0,
      colliNonAffidati: 0, colliDanneggiati: 0, immaginiDisponibili: 0
    };
  }

  // ─── DOWNLOAD EXCEL / IMMAGINI ───
  downloadExcel(): void {
    if (this.isDownloadingExcel) return;
    this.isDownloadingExcel = true;
    this.discrepancyService.exportExcel(this.buildSearchRequest()).pipe(
      finalize(() => { this.isDownloadingExcel = false; })
    ).subscribe({
      next: blob => {
        // Stesso formato di nome file usato dal backend (comparison_list_yyyyMMdd_HHmmss.xlsx)
        const now = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
        FileSaver.saveAs(blob, `comparison_list_${timestamp}.xlsx`);
      },
      error: () => this.notificationService.error('Errore durante il download del file Excel.')
    });
  }

  downloadImages(): void {
    if (this.isDownloadingImages) return;
    this.isDownloadingImages = true;
    this.discrepancyService.downloadImages(this.buildSearchRequest()).pipe(
      finalize(() => { this.isDownloadingImages = false; })
    ).subscribe({
      next: blob => {
        const timestamp = new Date().toISOString().slice(0, 10);
        FileSaver.saveAs(blob, `immagini_discrepanze_${timestamp}.zip`);
      },
      error: () => this.notificationService.error('Errore durante il download delle immagini.')
    });
  }

  resetFilters(): void {
    this.selectedClientCode     = '';
    this.selectedWarehouseCode  = '';
    this.columns                = [];
    this.initDefaultDates();
    this.fpFrom?.setDate(this.dateFrom!, true);
    this.fpTo?.setDate(this.dateTo!, true);
    this.rows                   = [];
    this.totalRecords           = 0;
    this.hasSearchBeenPerformed = false;
    this.errorMessage           = '';
    this.summary                = this.emptySummary();
  }

  // ─── SORT ───
  sortBy(field: string): void {
    if (this.sortField === field) { this.sortAsc = !this.sortAsc; }
    else { this.sortField = field; this.sortAsc = true; }
    if (this.hasSearchBeenPerformed) this.loadData();
  }

  getSortIcon(field: string): string {
    if (this.sortField !== field) return 'fas fa-sort text-muted';
    return this.sortAsc ? 'fas fa-sort-up text-primary' : 'fas fa-sort-down text-primary';
  }

  // ─── PAGINATION ───
  get totalPages(): number {
    return Math.ceil(this.totalRecords / this.itemsPerPage) || 1;
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const start = Math.max(1, this.currentPage - 2);
    const end   = Math.min(this.totalPages, start + 4);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.loadData();
  }

  setItemsPerPage(n: number): void {
    this.itemsPerPage = n;
    this.currentPage  = 1;
    if (this.hasSearchBeenPerformed) this.loadData();
  }

  // ─── CELL HELPERS ───
  getDiscrepancyBadgeClass(value: string): string {
    switch (value?.toUpperCase()) {
      case 'CRITICAL':  case 'CRITICA':  return 'disc-badge--critical';
      case 'PENDING':   case 'ATTESA':   return 'disc-badge--pending';
      case 'RESOLVED':  case 'RISOLTA':  return 'disc-badge--resolved';
      case 'MANCANTE':                   return 'disc-badge--critical';
      case 'ECCEDENTE':                  return 'disc-badge--warning';
      default:                           return 'disc-badge--info';
    }
  }

  getCellValue(row: DiscrepancyRow, col: DiscrepancyColumn): string {
    const v = row[col.key];
    if (v == null) return '-';
    if (col.type === 'date') {
      const d = new Date(v);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('it-IT', {
          day: '2-digit', month: '2-digit', year: '2-digit',
          hour: '2-digit', minute: '2-digit'
        });
      }
    }
    if (col.type === 'number') return Number(v).toLocaleString('it-IT');
    return String(v);
  }

  // ─── SECTION TOGGLES ───
  toggleFiltersCollapsed(): void { this.isFiltersCollapsed = !this.isFiltersCollapsed; }
  toggleReportCollapsed():  void { this.isReportCollapsed  = !this.isReportCollapsed;  }
}
